import { getValidatedLimitParam, getValidatedPageParam } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

export interface ICrmMasterCuisineData {
  id: number;
  name: string;
  region?: string;
  country?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ICrmMasterCuisinesResponse {
  data: ICrmMasterCuisineData[];
  total: number;
  pageCount: number;
  limit: number;
  page: number;
}

const endpoint = 'ums/v1/crm/management/master-cuisines';
export const getCrmMasterCuisinesEndpointIdentifier = endpoint;

type TData = ICrmMasterCuisinesResponse;
type TError = AxiosError;
type TQueryKey = [
  typeof endpoint,
  {
    page: TParam;
    limit: TParam;
    name?: TParam;
    region?: TParam;
    country?: TParam;
    isActive?: TParam;
  }
];

type Options = Omit<UseQueryOptions<TData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetCrmMasterCuisinesApi(options?: Options): ReturnType {
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { page, limit, name, region, country, isActive } = getPageParams();

  const queryKey: TQueryKey = [
    endpoint,
    {
      page,
      limit,
      name,
      region,
      country,
      isActive,
    },
  ];

  const queryFn = async () => {
    const params: any = {
      page: getValidatedPageParam(),
      limit: getValidatedLimitParam(),
    };

    if (name) {
      params.name = String(name);
    }

    if (region) {
      params.region = String(region);
    }

    if (country) {
      params.country = String(country);
    }

    if (isActive !== undefined && isActive !== null) {
      params.isActive = Boolean(isActive);
    }

    const { data } = await http.get(endpoint, { params });
    return data;
  };

  return useQueryWrapper(queryKey, queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
